import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import {
  WsmErrorFragment,
  WsmFitmentApplicationFragment,
  WsmFitmentAxisFragment,
  WsmFitmentSchemaCreateInput,
  WsmFitmentSchemaFragment,
  WsmFitmentSchemaInput,
  WsmFitmentValueKind,
} from "@dashboard/graphql";

import { WsmFitmentSchemaFormData, WsmFitmentValuePick, WsmFitmentWriteResult } from "./types";

/** Position is presentation only; the list a merchant sees is ordered by it. */
export const sortSchemaAxes = <T extends { position: number }>(axes: T[]): T[] =>
  [...axes].sort((a, b) => a.position - b.position);

/** The axes of a schema, in ask order. */
export const getSchemaAxes = (schema: WsmFitmentSchemaFragment): WsmFitmentAxisFragment[] =>
  sortSchemaAxes(schema.axes).map(entry => entry.axis);

/** What a merchant reads for an axis, schema or qualifier: its label, else its name. */
export const getFitmentLabel = (row: { name: string; displayLabel: string }): string =>
  row.displayLabel.trim() || row.name;

export const emptySchemaFormData = (): WsmFitmentSchemaFormData => ({
  name: "",
  displayLabel: "",
  isActive: true,
  showAsFacet: true,
  searchableByKeyword: true,
  axisIds: [],
});

export const schemaToFormData = (schema: WsmFitmentSchemaFragment): WsmFitmentSchemaFormData => ({
  name: schema.name,
  displayLabel: schema.displayLabel,
  isActive: schema.isActive,
  showAsFacet: schema.showAsFacet,
  searchableByKeyword: schema.searchableByKeyword,
  axisIds: getSchemaAxes(schema).map(axis => axis.id),
});

/**
 * The schema as the editor loaded it: the form's initial data and the axes a
 * save compares against come from this one copy, never from the live cache. A
 * newer axis set that reaches the cache meanwhile (someone else's save, a
 * refetch) then cannot make an untouched form look like an axis edit and send
 * the stale axes back. `revision` remounts the form when a save replaces it.
 */
export interface WsmFitmentSchemaSnapshot {
  revision: number;
  data: WsmFitmentSchemaFormData;
}

export const takeSchemaSnapshot = (
  schema: WsmFitmentSchemaFragment,
  previous?: WsmFitmentSchemaSnapshot,
): WsmFitmentSchemaSnapshot => ({
  revision: previous ? previous.revision + 1 : 0,
  data: schemaToFormData(schema),
});

/** Same axes in the same order: the save would not change the SchemaAxis rows. */
export const sameAxisOrder = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * What one save sends. A provided `axes` list replaces the set server-side, so
 * on an update it goes only when the on-screen order differs from
 * `loadedAxisIds`, the schema as loaded; a flag or name edit then cannot revert
 * an axis change someone else saved meanwhile. A create passes no
 * `loadedAxisIds` and always sends its axes: the server refuses a schema
 * without one, which is why that overload returns the create input.
 */
export function getSchemaInput(data: WsmFitmentSchemaFormData): WsmFitmentSchemaCreateInput;
export function getSchemaInput(
  data: WsmFitmentSchemaFormData,
  loadedAxisIds: string[],
): WsmFitmentSchemaInput;
export function getSchemaInput(
  data: WsmFitmentSchemaFormData,
  loadedAxisIds?: string[],
): WsmFitmentSchemaInput {
  return {
    name: data.name.trim(),
    displayLabel: data.displayLabel.trim(),
    isActive: data.isActive,
    showAsFacet: data.showAsFacet,
    searchableByKeyword: data.searchableByKeyword,
    ...(loadedAxisIds && sameAxisOrder(data.axisIds, loadedAxisIds)
      ? {}
      : { axes: data.axisIds.map((axis, position) => ({ axis, position })) }),
  };
}

/** Move the axis at `index` by `delta` rows; off the end is a no-op. */
export const moveAxis = (axisIds: string[], index: number, delta: number): string[] => {
  const target = index + delta;

  if (index < 0 || index >= axisIds.length || target < 0 || target >= axisIds.length) {
    return axisIds;
  }

  const next = [...axisIds];

  [next[index], next[target]] = [next[target], next[index]];

  return next;
};

/**
 * The id a macaw Select hands its onChange. With a string `value` prop it
 * passes the option's value; with `value={null}` (a picker that never holds a
 * value) it passes the whole `{ value, label }` option. Accept both.
 */
export const getPickedOptionValue = (picked: unknown): string | undefined => {
  if (typeof picked === "string") {
    return picked || undefined;
  }

  if (picked && typeof picked === "object" && "value" in picked) {
    const { value } = picked as { value: unknown };

    return typeof value === "string" && value ? value : undefined;
  }

  return undefined;
};

/** The axis list after the add picker fires: the pick appended once, else unchanged. */
export const appendPickedAxis = (axisIds: string[], picked: unknown): string[] => {
  const id = getPickedOptionValue(picked);

  return id && !axisIds.includes(id) ? [...axisIds, id] : axisIds;
};

/** The axes the picker still offers: every axis the schema does not have yet. */
export const getAxesToAdd = (
  axes: WsmFitmentAxisFragment[],
  axisIds: string[],
): WsmFitmentAxisFragment[] => axes.filter(axis => !axisIds.includes(axis.id));

/** One value the add dialog sends, and the textarea line it came from (1-based). */
export interface WsmFitmentValueLine {
  value: string;
  line: number;
}

/**
 * One value per line, as the backend keeps them: stripped, inner whitespace
 * collapsed, never blank, one spelling per value ignoring case. The backend
 * applies the same rule again (`normalize_value`), so this only saves a round
 * trip that would have come back as "already there". Each value keeps the
 * line it was typed on, so an error on the Nth value sent names that line.
 */
export const parseValueLinesWithNumbers = (text: string): WsmFitmentValueLine[] => {
  const seen = new Set<string>();
  const parsed: WsmFitmentValueLine[] = [];

  text.split(/\r?\n/).forEach((raw, index) => {
    const value = raw.trim().replace(/\s+/g, " ");

    if (value && !seen.has(value.toLowerCase())) {
      seen.add(value.toLowerCase());
      parsed.push({ value, line: index + 1 });
    }
  });

  return parsed;
};

export const parseValueLines = (text: string): string[] =>
  parseValueLinesWithNumbers(text).map(entry => entry.value);

/**
 * A bulk write's errors, each tied back to the row it names: the value bulk
 * create files a row's error under `values.<i>` (a bulk with `items` under
 * `items.<i>`), where i indexes what was sent. An error on no row stays unplaced.
 */
export const placeBulkErrors = (
  errors: WsmErrorFragment[],
  sent: WsmFitmentValueLine[],
): Array<{ error: WsmErrorFragment; row?: WsmFitmentValueLine }> =>
  errors.map(error => {
    const match = error.field?.match(/^(?:values|items)\.(\d+)(?:\.|$)/);

    return { error, row: match ? sent[Number(match[1])] : undefined };
  });

/**
 * An application's values laid out under the schema's axes, in ask order: one
 * cell per axis, empty where the application leaves that axis unconstrained.
 */
export const getApplicationCells = (
  application: Pick<WsmFitmentApplicationFragment, "values">,
  axes: Array<Pick<WsmFitmentAxisFragment, "id">>,
): string[] =>
  axes.map(axis => application.values.find(value => value.axis.id === axis.id)?.value ?? "");

/** "Cab: Crew Cab, Bed: Long" - the qualifier values that narrow an application. */
export const formatQualifierValues = (
  qualifierValues: WsmFitmentApplicationFragment["qualifierValues"],
): string =>
  qualifierValues
    .map(qualifierValue => `${getFitmentLabel(qualifierValue.qualifier)}: ${qualifierValue.value}`)
    .join(", ");

/** "2019 / Ford / F-350 (Cab: Crew Cab)" - how a delete dialog names an application. */
export const formatApplication = (
  application: WsmFitmentApplicationFragment,
  axes: Array<Pick<WsmFitmentAxisFragment, "id">>,
): string => {
  const values = getApplicationCells(application, axes)
    .filter(cell => cell)
    .join(" / ");
  const qualifiers = formatQualifierValues(application.qualifierValues);

  return qualifiers ? `${values} (${qualifiers})` : values;
};

/**
 * The picks that still need a value row: text the merchant typed that matched
 * no option. Keyed by axis (or qualifier) id, one text each, blanks dropped.
 */
export const getPicksToCreate = (
  picks: Record<string, WsmFitmentValuePick | undefined>,
): Array<{ owner: string; text: string }> =>
  Object.entries(picks)
    .filter((entry): entry is [string, WsmFitmentValuePick] => !!entry[1])
    .filter(([, pick]) => !pick.id && !!pick.text.trim())
    .map(([owner, pick]) => ({ owner, text: pick.text.trim() }));

/** The ids a bulk create sends, once every typed value has been resolved. */
export const getPickIds = (
  picks: Record<string, WsmFitmentValuePick | undefined>,
  resolved: Record<string, string>,
): string[] =>
  Object.entries(picks)
    .map(([owner, pick]) => pick?.id ?? (pick?.text.trim() ? resolved[owner] : undefined))
    .filter((id): id is string => !!id);

export const valueKindChoices = (labels: { text: string; number: string }) => [
  { value: WsmFitmentValueKind.TEXT, label: labels.text },
  { value: WsmFitmentValueKind.NUMBER, label: labels.number },
];

/** A mutation payload read as a write result: no payload is a failure too. */
export const toWriteResult = (
  payload: { errors: WsmErrorFragment[] } | null | undefined,
): WsmFitmentWriteResult => ({
  saved: !!payload && !payload.errors.length,
  errors: payload?.errors ?? [],
});

/**
 * A form's savebar state from its mutation: idle, in flight, then saved only
 * when the call came back with a payload and no errors. `getMutationState`
 * reads a transport failure (no data at all) as success; this reads it as the
 * failure it is.
 */
export const getWriteState = (
  opts: { called: boolean; loading: boolean },
  payload: { errors: WsmErrorFragment[] } | null | undefined,
): ConfirmButtonTransitionState => {
  if (opts.loading) {
    return "loading";
  }

  if (!opts.called) {
    return "default";
  }

  return toWriteResult(payload).saved ? "success" : "error";
};

/**
 * Errors from another mutation re-filed under one field of this form: a value
 * bulk create names its row `values.0`, which the add-application dialog shows
 * under the axes or the qualifiers depending on which list it was resolving.
 */
export const refileErrors = (errors: WsmErrorFragment[], field: string): WsmErrorFragment[] =>
  errors.map(error => ({ ...error, field }));

/** Whether an option already spells `text`, ignoring case and outer space. */
export const hasExactOption = (options: Array<{ label: string }>, text: string): boolean => {
  const needle = text.trim().toLowerCase();

  return !!needle && options.some(option => option.label.trim().toLowerCase() === needle);
};

/**
 * Options in reading order, numbers by value: "2" before "10", so a Year axis
 * reads 1999, 2000, ... rather than in the (updatedAt, id) order the API pages
 * in. Sorts only what it is handed; see the picker for why that is one loaded set.
 */
export const sortValueOptions = <T extends { label: string }>(options: T[]): T[] =>
  [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }),
  );
